import React, { useRef, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import selectAnalytics from './selectAnalytics';
import Price from '../Product/Price';
import VideoPlayer from '../../hocs/HLSVideo/HLSVideo';
import { updateListItemCache } from '../../actions/checkout';
import { searchInput } from '../../actions/environment';
import { unique } from '../../utils/array';
import { addClass, removeClass } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate, prefix, translateObjectData, removeLanguagePrefix } from '../../utils/locale';
import { blackOrWhite, lowContrast, rootUrl, baseName } from '../../utils/string';
import s from './ProductItem.scss';

let productContainerEl = null;

const ProductItem = ({ variantClasses, content, setup, cache, currentPath, currentHash, currentView, canonical, searchInputText, imageSize, index = 1, showingGhost = false, favourites, ...props }) => {
	const productContainer = useRef(null);
	content = translateObjectData(setup, content);
	let productItemClasses = [s.productItem, 'u_productItem'];
	if (variantClasses) productItemClasses = [...productItemClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('productItem') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];
	const path = canonical || currentPath.indexOf('search') !== -1 ? translate(setup, content, 'canonicalUri') : `${currentPath}/${content.uri}${currentHash}`;

	if (content.productHidden) return null;

	if (!content.inStock) productItemClasses.push(s.productItem__soldOut);
	if (content.price.showAsOnSale) productItemClasses.push(s.productItem__sale);
	if (content.price.newProduct) productItemClasses.push(s.productItem__new);

	let badges = [];
	const labels = [];
	if (content.variant_coming_soon_bool) {
		badges.push(<span key="coming-soon-badge" className={[s.productItem_badge, s.productItem_badge__horizontal].join(' ')}>{ translate(setup, 'comingSoon') }</span>);
	} else if (content.inStock && content.price.newProduct) {
		badges.push(<span key="new-badge" className={s.productItem_badge}>{ translate(setup, 'new').split('').join('\n') }</span>);
	} else if (content?.badges) {
		Object.keys(content.badges).forEach((b) => {
			const badge = content.badges[b];
			const extraProps = {};
			if (badge?.text === '%') {
				const percentageOff = content.price.discountPercent || Math.round(100 - (content.price.priceAsNumber / content.price.priceBeforeDiscountAsNumber) * 100);
				if (percentageOff === 0) return;
				if (badge?.color) extraProps.style = { color: badge.color };
				if (badge.position === 'label') {
					labels.push(<span key={badge.text} className={[s.productItem_badge, s.productItem_badge__label, s.productItem_badge__percentage].join(' ')} {...extraProps}>{ `-${percentageOff}%` }</span>);
				} else {
					badges.push(<span key={badge.text} className={[s.productItem_badge, s.productItem_badge__percentage].join(' ')} {...extraProps}>{ `-${percentageOff}%` }</span>);
				}
			} else if (badge.position === 'label') {
				if (badge?.color) extraProps.style = { color: badge.color };
				labels.push(<span key={badge.text} className={[s.productItem_badge, s.productItem_badge__label].join(' ')} {...extraProps}>{ badge.text }</span>);
			} else {
				if (badge?.color) extraProps.style = { backgroundColor: badge.color, color: blackOrWhite(badge.color) };
				badges.push(<span key={badge.text} className={s.productItem_badge} {...extraProps}>{ badge.text }</span>);
			}
		});
	}

	if (Object.keys(content.categories).filter((c) => content.categories[c].uri.indexOf('spring-cleaning') !== -1).length > 0) {
		badges = [<span key="sale-badge" className={[s.productItem_badge, s.productItem_badge__sale].join(' ')}>{ translate(setup, 'sale') }</span>];
	}

	const clearSearch = () => {
		if (searchInputText !== '') {
			props.searchInput('');
			props.closeSearch();
		}
		// Clear queue in listing to get all batched impressions.
		selectAnalytics(content, setup, removeLanguagePrefix(currentView), index);
		props.updateListItemCache(content.groupId, removeLanguagePrefix(currentView));
	};

	const hoverHandler = () => {
		if (productContainer?.current) {
			if (productContainerEl) removeClass(productContainerEl, 'u_hovering');
			productContainerEl = productContainer?.current;
			addClass(productContainer.current, 'u_hovering');
		}
	};

	const depictListingObject = cache?.depictKeys?.[removeLanguagePrefix(currentView)]?.productOrder?.[content.groupId] || {};
	const productItemData = {
		'data-product-id': content.groupId,
		'data-product-index': index,
	};

	if (!canonical && depictListingObject.listingId) productItemData['data-product-listing-result-id'] = depictListingObject.listingId;
	let media = (depictListingObject?.image && depictListingObject.image.slice(0, 1)) || content.image.slice(0, 1);
	media = [`${rootUrl(content.image[0])}/${baseName((media?.[0] || '').replace('-original', ''))}`];
	if (content?.media?.plp) media = [...media, ...content.media.plp];
	if (content?.media?.ghost && showingGhost) media = [...content.media.ghost];
	media = unique(media);

	if (content.film && Object.keys(content.film).length > 0) {
		const contentFilm = Object.keys(content.film).filter((k) => content.film[k].plp && content.film[k].plp === '1').map((k) => content.film[k]).sort((a, b) => (parseInt((a.order || 0), 10) >= parseInt((b.order || 0), 10) ? 1 : -1));
		if (contentFilm.length > 0) {
			media = [contentFilm[0]];
			productItemClasses.push(s.productItem__film);
		}
	}

	const relatedVariants = content.relatedVariants ? content.relatedVariants.filter((rI) => {
		if (!cache.products[rI]) return false;
		if (cache.products[rI]?.productStatus_discontinued && !cache.products[rI]?.productStatus_limitedEdition && parseInt((cache.products[rI].inStock || 0), 10) === 0) return false; // eslint-disable-line camelcase
		if (content.gender && cache.products[rI].gender !== content.gender) return false;
		return true;
	}) : [];

	const applyFinalSale = content.productStatus_finalSale && content.price.showAsOnSale && translate(setup, 'finalSalePricelistIds').split(',').indexOf(setup.pricelist) !== -1;

	return (
		<div className={productItemClasses.join(' ')} ref={productContainer} {...productItemData}>
			<Link to={prefix(setup, path)} onClick={clearSearch} onTouchMove={hoverHandler} className={[s.productItem_images, (content?.media?.plp ? s.productItem_images__multiple : s.productItem_images__double), 'u_productImages'].join(' ')}>
				{
					media.map((image, i) => {
						if (!image) return null;
						if (image?.src?.type === 'file') return <VideoPlayer key={image.src.url} className={s.productItem_image} src={image.src.url} autoPlay loop playsInline muted poster={image?.poster?.url} />;
						return <img key={image} className={s.productItem_image} {...imageSizeTranslation(imageSize, { url: image, width: 800, height: 1200 }, '-', (index < 4 && i === 0 ? 'eager' : 'lazy'))} alt={`${content.brand} ${content.name}`} />;
					})
				}
			</Link>
			{
				relatedVariants.length > 0 && (
					<div className={s.productItem_relatedItems}>
						{
							relatedVariants.slice(0, 3).map((rI) => {
								let relatedItem = cache.products[rI];
								relatedItem = translateObjectData(setup, relatedItem);
								const relatedImage = (relatedItem?.image && relatedItem.image.slice(0, 1)) || false;
								const relatedPath = canonical ? relatedItem.canonicalUri : `${currentPath}/${relatedItem.uri}${currentHash}`;

								return (
									<Fragment key={rI}>
										<Link className={s.productItem_relatedItem} to={prefix(setup, relatedPath)} onClick={clearSearch}>
											{
												relatedItem.color && Object.keys(relatedItem.color).map((rIC) => {
													if (relatedItem.color[rIC].color.indexOf('linear') !== -1) return <span key={rIC} className={[s.productItem_relatedItem_swatch, (unique(relatedItem.color[rIC].color.match(/#\w+/g)).filter((cp) => lowContrast(cp)).length > 0 ? s.productItem_relatedItem_swatch__lowContrast : null)].join(' ')} style={{ backgroundImage: `${relatedItem.color[rIC].color}` }} />;
													if (relatedItem.color[rIC].image) return <span className={s.productItem_relatedItem_swatch} key={rIC} style={{ backgroundImage: `url('${relatedItem.color[rIC].image.url}')` }} />;
													return <span className={[s.productItem_relatedItem_swatch, (lowContrast(relatedItem.color[rIC].color, '#E4E4E4', 1.21) ? s.productItem_relatedItem_swatch__lowContrast : null)].join(' ')} key={rIC} style={{ backgroundColor: relatedItem.color[rIC].color }} />;
												})
											}
										</Link>
										{ relatedImage && <img key={relatedImage.slice(relatedImage.lastIndexOf('/') + 1)} {...imageSizeTranslation(imageSize, { url: (relatedImage?.[0] || ''), width: 800, height: 1200 })} className={s.productItem_relatedImage} alt={`${content.brand} ${content.name}`} /> }
									</Fragment>
								);
							})
						}
						{
							relatedVariants.length > 3 && (
								<Link className={[s.productItem_relatedItem, s.productItem_relatedItem__plus].join(' ')} to={prefix(setup, path)} onClick={clearSearch}>
									<span className={s.productItem_relatedItem_swatch} />
								</Link>
							)
						}
					</div>
				)
			}
			<div className={s.productItem_badges}>
				{ badges }
			</div>
			<Link className={s.productItem_link} to={prefix(setup, path)} onClick={clearSearch}>
				<div className={s.productItem_link_info}>
					<div className={[s.productItem_link_info_details, applyFinalSale ? s.productItem_link_info_details__finalSale : null].join(' ')}>
						<div className={s.productItem_link_info_details_item}>
							<strong className={s.productItem_link_info_details_item_name}>{ content.name }</strong>
							<Price price={content.price} soldOut={!content.inStock ? translate(setup, 'soldOut') : false} plp />
							{ applyFinalSale && <span className={s.productItem_link_info_details_item_finalSale}><span>{ translate(setup, 'finalSale') }: </span>{ translate(setup, 'finalSaleLong') }</span> }
							{ labels }
						</div>
						{
							relatedVariants.length > 0 && (
								<div className={s.productItem_link_info_details_relations}>
									{
										relatedVariants.slice(0, 3).map((rI) => {
											let relatedItem = cache.products[rI];
											relatedItem = translateObjectData(setup, relatedItem);

											return (
												<span key={rI} className={s.productItem_link_info_details_relation}>
													{
														relatedItem.color && Object.keys(relatedItem.color).map((rIC) => {
															if (relatedItem.color[rIC].color.indexOf('linear') !== -1) return <span key={rIC} className={[s.productItem_link_info_details_relation_swatch, (unique(relatedItem.color[rIC].color.match(/#\w+/g)).filter((cp) => lowContrast(cp)).length > 0 ? s.productItem_link_info_details_relation_swatch__lowContrast : null)].join(' ')} style={{ backgroundImage: `${relatedItem.color[rIC].color}` }} />;
															if (relatedItem.color[rIC].image) return <span className={s.productItem_link_info_details_relation_swatch} key={rIC} style={{ backgroundImage: `url('${relatedItem.color[rIC].image.url}')` }} />;
															return <span className={[s.productItem_link_info_details_relation_swatch, (lowContrast(relatedItem.color[rIC].color, '#E4E4E4', 1.21) ? s.productItem_link_info_details_relation_swatch__lowContrast : null)].join(' ')} key={rIC} style={{ backgroundColor: relatedItem.color[rIC].color }} />;
														})
													}
												</span>
											);
										})
									}
									{
										relatedVariants.length > 3 && (
											<span className={[s.productItem_link_info_details_relation, s.productItem_link_info_details_relation__text].join(' ')}>+</span>
										)
									}
								</div>
							)
						}
					</div>
				</div>
			</Link>
			<Link className={s.productItem_shop} to={prefix(setup, path)} onClick={clearSearch}>{ translate(setup, 'shop') }</Link>
		</div>
	);
};

const mapState = ({ environment, productCache }, props) => ({
	setup: environment.setup,
	cache: productCache?.[environment.setup.market]?.[environment.setup.pricelist],
	searchInputText: environment.searchInputText,
	favourites: environment.favourites,
	...props,
});

export default hot(module)(connect(mapState, { searchInput, updateListItemCache })(ProductItem));
